import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Clock, Users, Bike, RefreshCw, Calendar, FileDown, ArrowUpRight, ArrowDownRight, Download, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Cell, AreaChart, Area } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fp = (p) => `${p?.toLocaleString() || 0}`;

const COLORS = ['#0A422D', '#D4A853', '#E16833', '#6366F1', '#2D6A4F', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'];
const SOURCE_LABELS = {
  dine_in: 'Sur place', takeaway: 'Emporter', click_collect: 'C&C', grab: 'Grab',
  foodpanda: 'Foodpanda', uber_eats: 'Uber Eats', deliveroo: 'Deliveroo', line_man: 'LINE MAN',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-[#E5DCD3] px-3 py-2">
      <p className="text-xs font-bold text-[#2D241D]">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>
          {p.name}: <strong>{fp(p.value)}</strong>
        </p>
      ))}
    </div>
  );
};

export const AnalyticsTab = () => {
  const [period, setPeriod] = useState('30d');
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [serverPerf, setServerPerf] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [weekComparison, setWeekComparison] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [revRes, topRes, peakRes, srvRes, platRes, weekRes] = await Promise.allSettled([
        axios.get(`${API}/admin/analytics-advanced/revenue-trend`, { params: { period } }),
        axios.get(`${API}/admin/analytics-advanced/top-products`, { params: { period, limit: 15 } }),
        axios.get(`${API}/admin/analytics-advanced/peak-hours`, { params: { period } }),
        axios.get(`${API}/admin/analytics-advanced/server-performance`, { params: { period } }),
        axios.get(`${API}/admin/analytics-advanced/platform-comparison`, { params: { period } }),
        axios.get(`${API}/admin/analytics-advanced/weekly-comparison`),
      ]);
      const g = (r) => r.status === 'fulfilled' ? r.value.data : [];
      setRevenueTrend(g(revRes));
      setTopProducts(g(topRes));
      setPeakHours(g(peakRes));
      setServerPerf(g(srvRes));
      setPlatforms(g(platRes));
      if (weekRes.status === 'fulfilled') setWeekComparison(weekRes.value.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [period]);

  const totalPeriodRevenue = revenueTrend.reduce((s, d) => s + d.revenue, 0);
  const totalPeriodOrders = revenueTrend.reduce((s, d) => s + d.count, 0);
  const peakHour = peakHours.reduce((max, h) => h.orders > (max?.orders || 0) ? h : max, null);

  if (loading) return <div className="flex items-center justify-center py-16"><RefreshCw className="w-6 h-6 animate-spin text-[#6E5E52]" /></div>;

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      {/* Header */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-[#6366F1]" />
          <h3 className="font-bold text-[#2D241D]">Analytics avance</h3>
        </div>
        <div className="flex gap-2">
          {[{ v: '7d', l: '7 jours' }, { v: '30d', l: '30 jours' }, { v: '90d', l: '3 mois' }].map(p => (
            <button key={p.v} onClick={() => setPeriod(p.v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${period === p.v ? 'bg-[#6366F1] text-white' : 'bg-[#F3EDE5] text-[#6E5E52] hover:bg-[#E5DCD3]'}`}
              data-testid={`analytics-period-${p.v}`}>
              {p.l}
            </button>
          ))}
          <Button onClick={fetchData} variant="outline" className="rounded-xl gap-1 text-xs" data-testid="analytics-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Actualiser
          </Button>
          <Button onClick={() => { window.open(`${API}/admin/analytics-advanced/export-csv?period=${period}`, '_blank'); toast.success('Export CSV lance'); }}
            variant="outline" className="rounded-xl gap-1 text-xs border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white"
            data-testid="analytics-export-csv">
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
          <Button onClick={() => { window.open(`${API}/admin/analytics-advanced/export-pdf?period=${period}`, '_blank'); toast.success('Export PDF lance'); }}
            variant="outline" className="rounded-xl gap-1 text-xs border-[#E16833] text-[#E16833] hover:bg-[#E16833] hover:text-white"
            data-testid="analytics-export-pdf">
            <FileText className="w-3.5 h-3.5" /> PDF
          </Button>
        </div>
      </div>

      {/* KPIs row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#0A422D] rounded-2xl p-4 text-white">
          <p className="text-xs opacity-70 mb-1">CA periode</p>
          <p className="text-2xl font-black" data-testid="analytics-total-revenue">{fp(totalPeriodRevenue)}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
          <p className="text-xs text-[#6E5E52] mb-1">Commandes</p>
          <p className="text-2xl font-black text-[#2D241D]">{fp(totalPeriodOrders)}</p>
        </div>
        {weekComparison && (
          <>
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
              <p className="text-xs text-[#6E5E52] mb-1">Cette semaine</p>
              <p className="text-xl font-black text-[#2D241D]">{fp(weekComparison.this_week.revenue)}</p>
              <div className={`flex items-center gap-0.5 text-xs font-bold mt-1 ${weekComparison.revenue_change_percent >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                {weekComparison.revenue_change_percent >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {weekComparison.revenue_change_percent}% vs semaine derniere
              </div>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
              <p className="text-xs text-[#6E5E52] mb-1">Heure de pointe</p>
              <p className="text-2xl font-black text-[#D4A853]">{peakHour ? peakHour.label : '-'}</p>
              <p className="text-[10px] text-[#6E5E52]">{peakHour ? `${peakHour.orders} commandes` : ''}</p>
            </div>
          </>
        )}
      </div>

      {/* Revenue Trend Chart */}
      <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
        <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
          <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#0A422D]" /> Evolution du chiffre d'affaires
          </h4>
        </div>
        <div className="p-4 h-[250px]" data-testid="analytics-revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5DCD3" />
              <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={v => fp(v)} tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0A422D" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0A422D" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="revenue" name="CA" stroke="#0A422D" strokeWidth={2} fill="url(#colorRevenue)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Products */}
        <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
          <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
            <h4 className="text-sm font-bold text-[#2D241D]">Top 15 produits vendus</h4>
          </div>
          <div className="p-4 h-[350px]" data-testid="analytics-top-products-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topProducts} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5DCD3" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="quantity" name="Quantite" radius={[0, 4, 4, 0]}>
                  {topProducts.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Peak Hours */}
        <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
          <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
            <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2"><Clock className="w-4 h-4 text-[#E16833]" /> Heures de pointe</h4>
          </div>
          <div className="p-4 h-[350px]" data-testid="analytics-peak-hours-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={peakHours}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5DCD3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="orders" name="Commandes" fill="#E16833" radius={[4, 4, 0, 0]}>
                  {peakHours.map((entry, i) => (
                    <Cell key={i} fill={entry === peakHour ? '#D4A853' : '#E16833'} opacity={entry.orders > 0 ? 1 : 0.2} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Server Performance */}
        <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
          <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
            <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2"><Users className="w-4 h-4 text-[#D4A853]" /> Performance serveurs</h4>
          </div>
          <div className="divide-y divide-[#F3EDE5]" data-testid="analytics-server-perf">
            {serverPerf.map((s, i) => (
              <div key={s.server} className="px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? 'bg-[#D4A853] text-black' : 'bg-[#F3EDE5] text-[#6E5E52]'}`}>{i + 1}</span>
                    <span className="text-sm font-bold text-[#2D241D]">{s.server}</span>
                  </div>
                  <span className="text-sm font-black text-[#0A422D]">{fp(s.revenue)}</span>
                </div>
                <div className="flex gap-4 text-[10px] text-[#6E5E52]">
                  <span>{s.orders} cmd</span>
                  <span>{s.items_sold} articles</span>
                  <span>Moy: {fp(s.avg_ticket)}</span>
                  <span>{s.dine_in} place / {s.takeaway} emporter</span>
                </div>
                <div className="mt-1 h-1.5 bg-[#F3EDE5] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{
                    width: `${Math.min((s.revenue / (serverPerf[0]?.revenue || 1)) * 100, 100)}%`,
                    backgroundColor: COLORS[i % COLORS.length]
                  }} />
                </div>
              </div>
            ))}
            {serverPerf.length === 0 && <p className="text-center text-xs text-[#6E5E52] py-6">Aucune donnee</p>}
          </div>
        </div>

        {/* Platform Comparison */}
        <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
          <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
            <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2"><Bike className="w-4 h-4 text-[#E16833]" /> Comparaison sources</h4>
          </div>
          <div className="divide-y divide-[#F3EDE5]" data-testid="analytics-platform-comparison">
            {platforms.map((p, i) => (
              <div key={p.source} className="px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-bold text-[#2D241D]">{SOURCE_LABELS[p.source] || p.source}</span>
                  <div className="text-right">
                    <span className="text-sm font-black text-[#0A422D]">{fp(p.revenue)}</span>
                    {p.commission > 0 && <span className="text-[10px] text-red-500 ml-1">(-{fp(p.commission)} com.)</span>}
                  </div>
                </div>
                <div className="flex gap-4 text-[10px] text-[#6E5E52]">
                  <span>{p.orders} commandes</span>
                  <span>Net: {fp(p.net_revenue)}</span>
                </div>
                <div className="mt-1 h-1.5 bg-[#F3EDE5] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{
                    width: `${Math.min((p.revenue / (platforms[0]?.revenue || 1)) * 100, 100)}%`,
                    backgroundColor: COLORS[i % COLORS.length]
                  }} />
                </div>
              </div>
            ))}
            {platforms.length === 0 && <p className="text-center text-xs text-[#6E5E52] py-6">Aucune donnee</p>}
          </div>
        </div>
      </div>
    </div>
  );
};
